import {
    CART_RELOAD_TIME_REDIRECTION_MS,
    ERROR_TEXT,
    GIFTING_SAVINGS_PLACEHOLDER,
} from "@cart-client/constants/Coupon";
import {
    BUSINESS_LINES,
    COUPON_CARD_COMMS_TYPES,
    IAppliedCouponCodes,
    GiftingApplyCouponWidgetInfo,
    ReviewedCartApiResponse,
    GiftingCartApiResponse,
    GiftingCouponCode,
} from "@swiggy-private/cart-types";
import {
    ICouponCard,
    COUPON_GROUP_TYPES,
    CouponInfo,
    CouponMessageCard,
    INextBestCoupon,
} from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { CouponsAnalyticsExtraContext } from "@cart-client/interfaces/Coupon";
import { buildGiftingCouponCodeList } from "./Coupon";

export const isBestCouponGroup = (type: COUPON_GROUP_TYPES) => {
    return type === COUPON_GROUP_TYPES.BEST_COUPON;
};

export const isGreatDealCouponGroup = (type: COUPON_GROUP_TYPES) => {
    return type === COUPON_GROUP_TYPES.GREAT_DEALS;
};

export const isPaymentOfferCouponGroup = (type?: COUPON_GROUP_TYPES): boolean => {
    return type === COUPON_GROUP_TYPES.PAYMENT_OFFERS;
};

export const getCouponSuggestionErrorCard = (
    couponError: CouponMessageCard,
    nextBest: INextBestCoupon | null,
): string => {
    let errorMessage = `${couponError.title} ${couponError.description}. `;
    if (nextBest) {
        errorMessage += isBestCouponGroup(nextBest.type) ? ERROR_TEXT.TRY_THIS : ERROR_TEXT.CONSIDER_THIS;
    }
    return errorMessage;
};

export const shouldSkipCartReloadOnRedirection = (launchTimestamp: number): boolean => {
    const timeDiff = Date.now() - launchTimestamp;
    const skipCartReload = timeDiff <= CART_RELOAD_TIME_REDIRECTION_MS;
    return skipCartReload;
};

export function getPaymentOfferCoupons(coupons?: ICouponCard[] | null) {
    return coupons?.find((coupon: ICouponCard) => coupon.type === COUPON_GROUP_TYPES.PAYMENT_OFFERS);
}

export const isCouponHeaderSectionComms = (type?: COUPON_CARD_COMMS_TYPES): boolean => {
    return type === COUPON_CARD_COMMS_TYPES.HEADER_COMMS;
};

export const getUpdatedAppliedCoupons = (
    coupons?: ICouponCard[] | null,
    couponSelections?: Map<string, boolean>,
): IAppliedCouponCodes[] => {
    if (isEmpty(coupons)) return [];

    // First, update the coupons with selection state if couponSelections is provided
    const updatedCoupons = couponSelections
        ? coupons.map((couponCard) => ({
              ...couponCard,
              coupons: couponCard.coupons?.map((coupon: CouponInfo) => ({
                  ...coupon,
                  // Use nullish coalescing - if key doesn't exist, fall back to original isSelected
                  isSelected: couponSelections.get(coupon.id || "") ?? (coupon.isSelected || false),
              })),
          }))
        : coupons;

    // Flatten all coupons from all cards (using reduce + concat for better browser support)
    const allCoupons = updatedCoupons.reduce(
        (acc: CouponInfo[], card: ICouponCard) => acc.concat(card.coupons || []),
        [],
    );

    // Filter only selected coupons
    const selectedCoupons = allCoupons.filter((coupon: CouponInfo) => coupon.isSelected === true);

    // Transform to the required format
    const appliedCoupons = selectedCoupons.map((coupon: CouponInfo) => ({
        businessLine: coupon.businessLine || "",
        code: coupon.title || "",
    }));

    return appliedCoupons;
};

export const getAppliedCoupons = (couponCodes?: GiftingCouponCode[] | null) => {
    return couponCodes?.map((coupon: GiftingCouponCode) => ({
        businessLine: coupon.businessLine || "",
        code: coupon.code || "",
    }));
};

/**
 * Get coupon IDs from the same business line that should be deselected
 * when selecting a coupon with a specific business line.
 *
 * @param coupons - Array of coupon cards containing all coupons
 * @param businessLine - The business line of the currently selected coupon
 * @param excludeCouponId - The coupon ID to exclude from deselection (current coupon)
 * @returns Map of coupon IDs to deselect (set to false)
 */
export const getOtherBusinessLineCouponsToDeselect = (
    coupons: ICouponCard[] | null,
    businessLine: BUSINESS_LINES | string,
    excludeCouponId: string,
): Map<string, boolean> => {
    const deselectMap = new Map<string, boolean>();

    if (isEmpty(coupons) || isEmpty(businessLine)) {
        return deselectMap;
    }

    // Flatten all coupons from all cards (using reduce + concat for better browser support)
    const allCoupons = coupons.reduce((acc: CouponInfo[], card: ICouponCard) => acc.concat(card.coupons || []), []);

    // Filter coupons matching the business line (excluding the current coupon)
    const matchingCoupons = allCoupons.filter(
        (coupon) => coupon.businessLine === businessLine && coupon.id !== excludeCouponId && coupon.id,
    );

    // Create map entries to deselect these coupons
    matchingCoupons.forEach((coupon) => deselectMap.set(coupon.id || "", false));

    return deselectMap;
};

/**
 * Check if all coupons across all sections have applicable set to false.
 *
 * @param coupons - Array of coupon cards containing all coupons
 * @returns boolean - true if all coupons have applicable: true, false if any coupon is not applicable
 */
export const areAllCouponsNotApplicable = (coupons: ICouponCard[] | null) => {
    if (isEmpty(coupons)) {
        return false;
    }

    // Flatten all coupons from all cards (using reduce + concat for better browser support)
    const allCoupons = coupons.reduce((acc: CouponInfo[], card: ICouponCard) => acc.concat(card.coupons || []), []);
    return allCoupons.length > 0 && !allCoupons.every((coupon) => coupon.applicable === false);
};

export const processSavingsTemplate = (template: string, totalSavings: number, couponCount: number): string => {
    let processedText = template;

    if (processedText.includes(GIFTING_SAVINGS_PLACEHOLDER.TOTAL_SAVINGS)) {
        processedText = processedText.replace(GIFTING_SAVINGS_PLACEHOLDER.TOTAL_SAVINGS, String(totalSavings));
    }

    if (processedText.includes(GIFTING_SAVINGS_PLACEHOLDER.COUPON_COUNT)) {
        processedText = processedText.replace(GIFTING_SAVINGS_PLACEHOLDER.COUPON_COUNT, String(couponCount));
    }

    return processedText;
};

export const calculateAppliedCouponsSavings = (
    coupons: ICouponCard[] | null,
    appliedCouponCodes: IAppliedCouponCodes[],
) => {
    if (isEmpty(coupons) || isEmpty(appliedCouponCodes)) {
        return 0;
    }

    let totalSavings = 0;

    // Create a map using composite key (businessLine:code) to avoid collisions
    // between different business lines with same coupon code (e.g., TRYNEW in Food vs IM)
    const appliedCodesMap = new Map(
        appliedCouponCodes.map((coupon) => [`${coupon.businessLine}:${coupon.code}`, coupon]),
    );

    // Flatten all coupons and calculate savings for applied ones
    coupons.forEach((card) => {
        card.coupons?.forEach((coupon: CouponInfo) => {
            // Create composite key for lookup to match businessLine + code
            const compositeKey = `${coupon.businessLine || ""}:${coupon.title || ""}`;
            const isApplied = appliedCodesMap.has(compositeKey);

            if (isApplied && coupon.absoluteSavings) {
                totalSavings += coupon.absoluteSavings;
            }
        });
    });

    return Math.floor(totalSavings);
};

export const calculateBestCouponsSavings = (coupons: ICouponCard[] | null) => {
    if (isEmpty(coupons)) {
        return { totalSavings: 0, couponCount: 0 };
    }

    let totalSavings = 0;
    let couponCount = 0;

    coupons.forEach((card) => {
        card.coupons?.forEach((coupon: CouponInfo) => {
            if (coupon.isBestCoupon && coupon.absoluteSavings) {
                totalSavings += coupon.absoluteSavings;
                couponCount += 1;
            }
        });
    });

    return { totalSavings: Math.floor(totalSavings), couponCount };
};

export const getAppliedOfferIds = (
    appliedCouponCodes: IAppliedCouponCodes[],
    coupons?: ICouponCard[] | null,
): string[] => {
    if (isEmpty(appliedCouponCodes) || isEmpty(coupons)) {
        return [];
    }

    const appliedOfferIds: string[] = [];

    // Create a set of composite keys for faster lookup
    const appliedCodesSet = new Set(
        appliedCouponCodes.map((appliedCoupon) => `${appliedCoupon.businessLine}:${appliedCoupon.code}`),
    );

    coupons.forEach((card) => {
        card.coupons?.forEach((coupon) => {
            // Check if this coupon matches any applied coupon (considering business line)
            const compositeKey = `${coupon.businessLine || ""}:${coupon.title || ""}`;
            const isMatchedByCompositeKey = appliedCodesSet.has(compositeKey);

            if (isMatchedByCompositeKey && coupon.id) {
                appliedOfferIds.push(coupon.id);
            }
        });
    });

    return appliedOfferIds;
};

export const getAppliedGiftingCoupons = (cartData?: GiftingCartApiResponse): IAppliedCouponCodes[] | undefined => {
    if (cartData && "couponCodes" in cartData) {
        return getAppliedCoupons(cartData.couponCodes);
    }
    return getAppliedCoupons();
};

export const extractGiftingCouponCodes = (applyCouponWidgetData?: GiftingApplyCouponWidgetInfo) => {
    return applyCouponWidgetData?.coupon_codes || [];
};

export const fetchCouponCodeListFromResponse = (
    cartResponse: ReviewedCartApiResponse,
): CouponsAnalyticsExtraContext => {
    const { couponList: couponListFromGlobalCoupons } = buildGiftingCouponCodeList(cartResponse) || {};
    const couponListFromGiftingCart = extractGiftingCouponCodes(cartResponse?.apply_coupon_widget_info) || [];
    return {
        couponList: [...(couponListFromGlobalCoupons || []), ...(couponListFromGiftingCart || [])],
    };
};
